import { RotateCcw } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ApartmentFilterOptions } from '@services/livability/types'

import type { ApartmentFilters } from '../../utils/apartmentFilterUrl'
import { hasActiveApartmentFilters } from '../../utils/apartmentFilterUrl'
import { AddFilterMenu } from './AddFilterMenu'
import { FilterChip } from './FilterChip'
import {
  ALL_FILTER_DEFS,
  applyFilterPatch,
  clearFilterDef,
  getFilterDefById,
  isFilterDefActive,
  isFilterDefOffered,
  QUICK_FILTER_IDS,
  type FilterDef
} from './filterDefs'

interface FilterChipBarProps {
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
  onClearAll: () => void
  filterOptions?: ApartmentFilterOptions
  className?: string
}

export function FilterChipBar({
  filters,
  onChange,
  onClearAll,
  filterOptions,
  className
}: FilterChipBarProps) {
  const [pendingDefIds, setPendingDefIds] = useState<Set<string>>(
    () => new Set()
  )
  const [popoverDefId, setPopoverDefId] = useState<string | null>(null)

  const activeDefs = ALL_FILTER_DEFS.filter((def) =>
    isFilterDefActive(def, filters)
  )
  const activeIds = new Set(activeDefs.map((d) => d.id))

  const pendingDefs = [...pendingDefIds]
    .map((id) => getFilterDefById(id))
    .filter((def): def is FilterDef => Boolean(def && !activeIds.has(def.id)))

  const visibleDefs: FilterDef[] = [...activeDefs, ...pendingDefs]

  const reservedIds = new Set([...activeIds, ...pendingDefIds])

  const availableDefs = ALL_FILTER_DEFS.filter(
    (def) =>
      !reservedIds.has(def.id) &&
      isFilterDefOffered(def, filters, filterOptions)
  )

  const quickDefs = QUICK_FILTER_IDS.map((id) => getFilterDefById(id)).filter(
    (def): def is FilterDef =>
      Boolean(
        def &&
        !reservedIds.has(def.id) &&
        isFilterDefOffered(def, filters, filterOptions)
      )
  )

  const hasActive = hasActiveApartmentFilters(filters)

  const addPendingFilter = useCallback(
    (def: FilterDef) => {
      if (def.type === 'boolean') {
        onChange(applyFilterPatch(filters, { [def.key]: true }))
        return
      }
      setPendingDefIds((prev) => new Set(prev).add(def.id))
      requestAnimationFrame(() => setPopoverDefId(def.id))
    },
    [filters, onChange]
  )

  const clearPending = useCallback((defId: string) => {
    setPendingDefIds((prev) => {
      const next = new Set(prev)
      next.delete(defId)
      return next
    })
    setPopoverDefId((current) => (current === defId ? null : current))
  }, [])

  const promotePending = useCallback(
    (def: FilterDef, next: ApartmentFilters) => {
      if (isFilterDefActive(def, next)) {
        clearPending(def.id)
      }
      onChange(next)
    },
    [clearPending, onChange]
  )

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2 py-0.5">
        {visibleDefs.map((def) => {
          const isPending = pendingDefIds.has(def.id) && !activeIds.has(def.id)
          return (
            <div key={def.id} className="shrink-0">
              <FilterChip
                def={def}
                filters={filters}
                filterOptions={filterOptions}
                isPending={isPending}
                openPopover={def.id === popoverDefId}
                onPopoverOpened={() => {
                  setPopoverDefId((current) =>
                    current === def.id ? null : current
                  )
                }}
                onChange={(next) => promotePending(def, next)}
                onRemove={() => {
                  if (isFilterDefActive(def, filters)) {
                    onChange(clearFilterDef(def, filters))
                  }
                  clearPending(def.id)
                }}
              />
            </div>
          )
        })}

        {quickDefs.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label="Často používané filtre"
          >
            {quickDefs.map((def) => (
              <button
                key={def.id}
                type="button"
                onClick={() => addPendingFilter(def)}
                className={cn(
                  'rounded-full border border-dashed border-ink/20 px-3 py-1.5',
                  'text-sm font-medium text-muted-foreground',
                  'transition-colors hover:border-ink/35 hover:bg-ink/[0.03] hover:text-ink'
                )}
              >
                {def.label}
              </button>
            ))}
          </div>
        )}

        <AddFilterMenu
          availableDefs={availableDefs}
          filters={filters}
          filterOptions={filterOptions}
          onSelect={addPendingFilter}
        />

        {hasActive && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setPendingDefIds(new Set())
              setPopoverDefId(null)
              onClearAll()
            }}
            className="gap-1.5 text-muted-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Vymazať všetko
          </Button>
        )}
      </div>
    </div>
  )
}
